import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, TrendingUp } from 'lucide-react';
import StarRating from './StarRating';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Seção completa de avaliações para página de detalhes do curso
 */
const CourseReviewsSection = ({ courseId, courseName }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [canReview, setCanReview] = useState({ can_review: false, reason: null });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchReviewsData();
  }, [courseId]);

  const fetchReviewsData = async () => {
    setLoading(true);
    try {
      // Fetch reviews and stats in parallel
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reviews/course/${courseId}`),
        axios.get(`${API_URL}/api/reviews/course/${courseId}/stats`)
      ]);

      setReviews(reviewsRes.data);
      setStats(statsRes.data);

      // Check if user can review (only if logged in)
      if (user) {
        try {
          const canReviewRes = await axios.get(
            `${API_URL}/api/reviews/can-review/${courseId}`
          );
          setCanReview(canReviewRes.data);
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('Erro ao verificar elegibilidade de avaliação:', err);
          }
          setCanReview({ can_review: false, reason: 'error' });
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Erro ao carregar avaliações do curso:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmitted = (newReview) => {
    setReviews([newReview, ...reviews]);
    setShowForm(false);
    setCanReview({ can_review: false, reason: 'already_reviewed' });
    // Refresh stats
    fetchReviewsData();
  };

  const getRatingBarWidth = (count) => {
    if (!stats || stats.total_reviews === 0) return 0;
    return (count / stats.total_reviews) * 100;
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-zinc-800 rounded w-1/3"></div>
          <div className="h-24 bg-zinc-800 rounded"></div>
          <div className="h-32 bg-zinc-800 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8" data-testid="course-reviews-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-red-500" />
          Avaliações
          {stats && stats.total_reviews > 0 && (
            <span className="text-lg text-gray-400 font-normal">
              ({stats.total_reviews})
            </span>
          )}
        </h2>

        {/* Add Review Button */}
        {user && canReview.can_review && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            data-testid="add-review-btn"
          >
            Avaliar Curso
          </button>
        )}
      </div>

      {/* Stats Summary */}
      {stats && stats.total_reviews > 0 && (
        <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800 mb-6">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Average Rating */}
            <div className="flex flex-col items-center justify-center">
              <div className="text-5xl font-bold text-white mb-2">
                {stats.average_rating.toFixed(1)}
              </div>
              <StarRating rating={stats.average_rating} size="md" />
              <p className="text-sm text-gray-400 mt-2">
                {stats.total_reviews} {stats.total_reviews === 1 ? 'avaliação' : 'avaliações'}
              </p>
            </div>

            {/* Rating Distribution */}
            <div className="flex-1 space-y-2">
              {[5, 4, 3, 2, 1].map((stars) => (
                <div key={stars} className="flex items-center gap-3">
                  <span className="text-sm text-gray-400 w-12">{stars} {stars === 1 ? 'estrela' : 'estrelas'}</span>
                  <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                      style={{ width: `${getRatingBarWidth(stats.rating_distribution[stars])}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-8 text-right">
                    {stats.rating_distribution[stars]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Review Form */}
      {showForm && (
        <div className="mb-6">
          <ReviewForm 
            courseId={courseId}
            courseName={courseName}
            onReviewSubmitted={handleReviewSubmitted}
          />
          <button
            onClick={() => setShowForm(false)}
            className="mt-3 text-gray-400 hover:text-white text-sm"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Can't Review Messages */}
      {user && !canReview.can_review && canReview.reason === 'not_enrolled' && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 mb-6 text-center">
          <p className="text-gray-400">
            Matricule-se neste curso para poder avaliá-lo.
          </p>
        </div>
      )}

      {user && !canReview.can_review && canReview.reason === 'already_reviewed' && (
        <div className="bg-green-900/20 border border-green-800 rounded-lg p-4 mb-6 text-center">
          <p className="text-green-400">
            Você já avaliou este curso. Obrigado pelo feedback!
          </p>
        </div>
      )}

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
          <Star className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">
            Este curso ainda não possui avaliações.
          </p>
          {user && canReview.can_review && (
            <p className="text-gray-500 text-sm mt-2">
              Seja o primeiro a avaliar!
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseReviewsSection;
